import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import toast from 'react-hot-toast';
import {
  otherInfoSchema,
  type OtherInfoFormValues,
} from '../validation/otherInfoSchema';
import { RELATION_OPTIONS } from '../constants/otherInfoConstants';
import FormDropdown from '../../../components/common/FormDropdown';
import './OtherInfoStep.scss';

const TERMS_TEXT =
  "I accept CricCenter's Terms & Conditions, authorize the transfer of data from 3rd Party applications, and confirm that the information provided is accurate and complete.";

const emptyOther: OtherInfoFormValues = {
  emergencyContactName: '',
  relation: '',
  emergencyContactNumber: '',
  zipCode: '',
  state: '',
  address: '',
  description: '',
  acceptTerms: false,
};

interface OtherInfoStepProps {
  initialValues?: OtherInfoFormValues;
  onComplete: (values: OtherInfoFormValues) => void | Promise<void>;
}

function OtherInfoStep({ initialValues, onComplete }: OtherInfoStepProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors, isValid },
  } = useForm<OtherInfoFormValues>({
    resolver: yupResolver(otherInfoSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: initialValues ?? emptyOther,
  });

  useEffect(() => {
    reset(initialValues ?? emptyOther);
  }, [initialValues, reset]);

  const onSubmit = async (values: OtherInfoFormValues) => {
    setIsSubmitting(true);
    try {
      await onComplete(values);
    } catch {
      toast.error('Failed to save. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="other-info-form">
      <div className="other-info-grid">
        <div className="other-info-field">
          <label htmlFor="emergencyContactName">
            Emergency Contact Name <span className="req">*</span>
          </label>
          <input
            id="emergencyContactName"
            type="text"
            placeholder="Enter emergency contact name"
            className={errors.emergencyContactName ? 'error' : ''}
            {...register('emergencyContactName')}
          />
          {errors.emergencyContactName && (
            <span className="field-error">
              {errors.emergencyContactName.message}
            </span>
          )}
        </div>
        <div className="other-info-field">
          <label htmlFor="relation">
            Relation <span className="req">*</span>
          </label>
          <Controller
            name="relation"
            control={control}
            render={({ field }) => (
              <FormDropdown
                id="relation"
                ref={field.ref}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                options={RELATION_OPTIONS}
                hasError={!!errors.relation}
              />
            )}
          />
          {errors.relation && (
            <span className="field-error">{errors.relation.message}</span>
          )}
        </div>
      </div>

      <div className="other-info-field">
        <label htmlFor="emergencyContactNumber">
          Emergency Contact Number <span className="req">*</span>
        </label>
        <Controller
          name="emergencyContactNumber"
          control={control}
          render={({ field }) => (
            <PhoneInput
              country="in"
              value={field.value || ''}
              onChange={field.onChange}
              inputClass={`other-info-input phone-input-field ${errors.emergencyContactNumber ? 'error' : ''}`}
              containerClass="phone-input-container"
              placeholder="Enter emergency contact number"
              enableSearch
            />
          )}
        />
        {errors.emergencyContactNumber && (
          <span className="field-error">
            {errors.emergencyContactNumber.message}
          </span>
        )}
      </div>

      <div className="other-info-grid">
        <div className="other-info-field">
          <label htmlFor="zipCode">Zip Code</label>
          <input
            id="zipCode"
            type="text"
            placeholder="Enter Zip Code"
            {...register('zipCode')}
          />
        </div>
        <div className="other-info-field">
          <label htmlFor="state">State</label>
          <input
            id="state"
            type="text"
            placeholder="Enter State"
            {...register('state')}
          />
        </div>
      </div>

      <div className="other-info-field">
        <label htmlFor="address">
          Address (Max 150 Characters)
        </label>
        <textarea
          id="address"
          placeholder="Enter Address"
          rows={3}
          maxLength={150}
          {...register('address')}
        />
        {errors.address && (
          <span className="field-error">{errors.address.message}</span>
        )}
      </div>

      <div className="other-info-field">
        <label htmlFor="description">
          Description (Max 150 Characters)
        </label>
        <textarea
          id="description"
          placeholder="Enter Description"
          rows={3}
          maxLength={150}
          {...register('description')}
        />
        {errors.description && (
          <span className="field-error">{errors.description.message}</span>
        )}
      </div>

      <div className="other-info-field terms-field">
        <label className="terms-checkbox-label">
          <input
            type="checkbox"
            className={errors.acceptTerms ? 'error' : ''}
            {...register('acceptTerms')}
          />
          <span className="terms-text">{TERMS_TEXT}</span>
        </label>
        {errors.acceptTerms && (
          <span className="field-error">{errors.acceptTerms.message}</span>
        )}
      </div>

      <div className="other-info-actions">
        <button
          type="submit"
          className="btn-explore"
          disabled={isSubmitting || !isValid}
        >
          {isSubmitting ? 'Saving...' : 'Explore CricCenter'}
        </button>
      </div>
    </form>
  );
}

export default OtherInfoStep;
